export const BODY_CIRCLE = 0;
export const BODY_BOX = 1;

/**
 * Longitud de un vector 2D, con el MISMO resultado en todas las plataformas.
 *
 * No es `Math.hypot`. La especificación de ECMAScript solo pide que `hypot` sea
 * "implementation-approximated": cada motor puede devolver bits distintos en el
 * último dígito. Aquí eso no es una curiosidad numérica — la simulación corre a
 * la vez en el servidor (Bun, JavaScriptCore) y en el navegador de cada jugador
 * (V8, SpiderMonkey, JavaScriptCore), y el netcode entero se apoya en que las
 * dos ejecuciones den EXACTAMENTE lo mismo. Un bit de diferencia se amplifica
 * tick a tick hasta que el balón está en sitios distintos para cada uno.
 *
 * `*`, `+` y `sqrt` sí están obligadas por IEEE 754 a redondear exactamente, así
 * que esta forma es portable. Lo que se pierde de `hypot` es su protección
 * frente a desbordamiento con magnitudes extremas (~1e154), irrelevante en una
 * cancha que se mide en cientos de píxeles.
 */
export function length2(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

export const MAX_BODIES = 256;

export const LAYER_NONE = 0;
export const LAYER_BALL = 1;
export const LAYER_PLAYER = 2;
export const LAYER_ALL = LAYER_BALL | LAYER_PLAYER;

export interface World {
  count: number;
  px: Float64Array;
  py: Float64Array;
  vx: Float64Array;
  vy: Float64Array;
  radius: Float64Array;
  hx: Float64Array;
  hy: Float64Array;
  invMass: Float64Array;
  bounce: Float64Array;
  friction: Float64Array;
  kind: Uint8Array;
  drag: Float64Array;
  mask: Uint32Array;
}

export function createWorld(capacity: number = MAX_BODIES): World {
  return {
    count: 0,
    px: new Float64Array(capacity),
    py: new Float64Array(capacity),
    vx: new Float64Array(capacity),
    vy: new Float64Array(capacity),
    radius: new Float64Array(capacity),
    hx: new Float64Array(capacity),
    hy: new Float64Array(capacity),
    invMass: new Float64Array(capacity),
    bounce: new Float64Array(capacity),
    friction: new Float64Array(capacity),
    kind: new Uint8Array(capacity),
    drag: new Float64Array(capacity),
    mask: new Uint32Array(capacity),
  };
}

export function addCircle(
  w: World,
  x: number,
  y: number,
  r: number,
  mass: number,
  bounce: number,
  friction: number,
  drag: number,
  mask: number = LAYER_ALL,
): number {
  const i = w.count++;
  w.px[i] = x;
  w.py[i] = y;
  w.vx[i] = 0;
  w.vy[i] = 0;
  w.radius[i] = r;
  w.hx[i] = 0;
  w.hy[i] = 0;
  w.invMass[i] = mass > 0 ? 1 / mass : 0;
  w.bounce[i] = bounce;
  w.friction[i] = friction;
  w.kind[i] = BODY_CIRCLE;
  w.drag[i] = drag;
  w.mask[i] = mask;
  return i;
}

export function addBox(
  w: World,
  cx: number,
  cy: number,
  hx: number,
  hy: number,
  bounce: number,
  friction: number,
  mask: number = LAYER_ALL,
): number {
  const i = w.count++;
  w.px[i] = cx;
  w.py[i] = cy;
  w.vx[i] = 0;
  w.vy[i] = 0;
  w.radius[i] = 0;
  w.hx[i] = hx;
  w.hy[i] = hy;
  w.invMass[i] = 0;
  w.bounce[i] = bounce;
  w.friction[i] = friction;
  w.kind[i] = BODY_BOX;
  w.drag[i] = 0;
  w.mask[i] = mask;
  return i;
}

let mnNX = 0, mnNY = 0, mnPen = 0;

function circleCircle(w: World, a: number, b: number): boolean {
  const dx = w.px[b] - w.px[a];
  const dy = w.py[b] - w.py[a];
  const ra = w.radius[a];
  const rb = w.radius[b];
  const r = ra + rb;
  const d2 = dx * dx + dy * dy;
  if (d2 >= r * r) return false;
  let d = Math.sqrt(d2);
  if (d > 1e-9) {
    mnNX = dx / d;
    mnNY = dy / d;
  } else {
    mnNX = 1;
    mnNY = 0;
  }
  mnPen = r - d;
  return true;
}

function circleBox(w: World, c: number, b: number): boolean {
  const cx = w.px[c], cy = w.py[c];
  const bx = w.px[b], by = w.py[b];
  const hx = w.hx[b], hy = w.hy[b];
  const closestX = Math.max(bx - hx, Math.min(cx, bx + hx));
  const closestY = Math.max(by - hy, Math.min(cy, by + hy));
  const dx = closestX - cx;
  const dy = closestY - cy;
  const d2 = dx * dx + dy * dy;
  const r = w.radius[c];
  if (d2 >= r * r) return false;
  let d = Math.sqrt(d2);
  if (d > 1e-9) {
    mnNX = dx / d;
    mnNY = dy / d;
  } else {
    const left = cx - (bx - hx);
    const right = bx + hx - cx;
    const top = cy - (by - hy);
    const bottom = by + hy - cy;
    const min = Math.min(left, right, top, bottom);
    if (min === left) { mnNX = -1; mnNY = 0; }
    else if (min === right) { mnNX = 1; mnNY = 0; }
    else if (min === top) { mnNX = 0; mnNY = -1; }
    else { mnNX = 0; mnNY = 1; }
  }
  mnPen = r - d;
  return true;
}

function resolve(w: World, a: number, b: number): void {
  const imA = w.invMass[a];
  const imB = w.invMass[b];
  const imSum = imA + imB;
  if (imSum === 0) return;

  const nx = mnNX, ny = mnNY;

  const rvx = w.vx[b] - w.vx[a];
  const rvy = w.vy[b] - w.vy[a];
  const velAlongNormal = rvx * nx + rvy * ny;
  if (velAlongNormal > 0) {
    positionalCorrection(w, a, b, nx, ny);
    return;
  }

  const e = Math.min(w.bounce[a], w.bounce[b]);
  let j = -(1 + e) * velAlongNormal;
  j /= imSum;
  const ix = j * nx;
  const iy = j * ny;
  w.vx[a] -= ix * imA;
  w.vy[a] -= iy * imA;
  w.vx[b] += ix * imB;
  w.vy[b] += iy * imB;

  const tx = -ny;
  const ty = nx;
  const tvel = (w.vx[b] - w.vx[a]) * tx + (w.vy[b] - w.vy[a]) * ty;
  const fric = Math.sqrt(w.friction[a] * w.friction[b]);
  let jt = -tvel / imSum;
  const cap = Math.abs(j) * fric;
  if (jt > cap) jt = cap;
  else if (jt < -cap) jt = -cap;
  w.vx[a] -= jt * tx * imA;
  w.vy[a] -= jt * ty * imA;
  w.vx[b] += jt * tx * imB;
  w.vy[b] += jt * ty * imB;

  positionalCorrection(w, a, b, nx, ny);
}

function positionalCorrection(w: World, a: number, b: number, nx: number, ny: number): void {
  const percent = 0.8;
  const slop = 0.5;
  const imA = w.invMass[a];
  const imB = w.invMass[b];
  const imSum = imA + imB;
  if (imSum === 0) return;
  const corr = (Math.max(mnPen - slop, 0) / imSum) * percent;
  w.px[a] -= corr * nx * imA;
  w.py[a] -= corr * ny * imA;
  w.px[b] += corr * nx * imB;
  w.py[b] += corr * ny * imB;
}

export function step(w: World, dt: number, iterations: number = 4): void {
  const n = w.count;
  for (let i = 0; i < n; i++) {
    if (w.kind[i] === BODY_CIRCLE && w.invMass[i] > 0) {
      const d = w.drag[i];
      if (d > 0) {
        const f = Math.max(0, 1 - d * dt);
        w.vx[i] *= f;
        w.vy[i] *= f;
      }
      w.px[i] += w.vx[i] * dt;
      w.py[i] += w.vy[i] * dt;
    }
  }

  for (let it = 0; it < iterations; it++) {
    for (let a = 0; a < n; a++) {
      const ka = w.kind[a];
      for (let b = a + 1; b < n; b++) {
        if ((w.mask[a] & w.mask[b]) === 0) continue;
        const kb = w.kind[b];
        if (ka === BODY_CIRCLE && kb === BODY_CIRCLE) {
          if (circleCircle(w, a, b)) resolve(w, a, b);
        } else if (ka === BODY_CIRCLE && kb === BODY_BOX) {
          if (circleBox(w, a, b)) resolve(w, a, b);
        } else if (ka === BODY_BOX && kb === BODY_CIRCLE) {
          if (circleBox(w, b, a)) resolve(w, b, a);
        }
      }
    }
  }
}

export function setVelocity(w: World, i: number, vx: number, vy: number): void {
  w.vx[i] = vx;
  w.vy[i] = vy;
}

export function applyImpulse(w: World, i: number, ix: number, iy: number): void {
  const im = w.invMass[i];
  if (im === 0) return;
  w.vx[i] += ix * im;
  w.vy[i] += iy * im;
}

export function applyForce(w: World, i: number, fx: number, fy: number, dt: number): void {
  const im = w.invMass[i];
  if (im === 0) return;
  w.vx[i] += fx * im * dt;
  w.vy[i] += fy * im * dt;
}
